import { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import { Underline } from '@tiptap/extension-underline';
import { generateJSON } from '@tiptap/core';
import { Bold, Italic, Underline as UnderlineIcon, Strikethrough, Palette } from 'lucide-react';
import { Toggle } from '@/components/ui/toggle';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { sanitizeHtml, normalizeHtml, wrapPlainText } from '@/lib/html-utils';
import { useTranslation } from 'react-i18next';

/**
 * Predefined color palette for text formatting
 * Uses Tailwind color values for consistency with better contrast
 */
const COLORS = [
  { name: 'Black', value: '#000000' },
  { name: 'Red', value: '#DC2626' },      // Darker red for better readability
  { name: 'Orange', value: '#EA580C' },   // Darker orange
  { name: 'Yellow', value: '#CA8A04' },   // Darker yellow
  { name: 'Green', value: '#16A34A' },    // Darker green
  { name: 'Blue', value: '#2563EB' },     // Darker blue
  { name: 'Purple', value: '#9333EA' },   // Darker purple
];

/**
 * Extracts simplified formatting from pasted HTML content
 *
 * Processes HTML from sources like Excel/Word and extracts only:
 * - Text content
 * - Bold/italic/underline/strikethrough formatting
 * - Text colors
 *
 * Strips out:
 * - Tables and table structure
 * - Images
 * - Complex layout elements
 * - Background colors
 * - Fonts and sizes
 *
 * Handles both Mac and Windows Excel HTML formats:
 * - Mac: Simple HTML with inline styling
 * - Windows: Complex HTML with MSO tags, style classes, and table structure
 *
 * @param element - DOM element containing the pasted HTML
 * @returns Simplified HTML string with basic formatting preserved
 */
const extractSimplifiedFormatting = (element: HTMLElement): string => {
  const result: string[] = [];

  /**
   * Parse CSS to extract formatting from class definitions
   * Handles Windows Excel format with .font* classes and other style classes
   */
  const parseCssStyles = (element: HTMLElement): Map<string, { color?: string; fontWeight?: string }> => {
    const styleMap = new Map<string, { color?: string; fontWeight?: string }>();
    const styleElements = element.querySelectorAll('style');

    styleElements.forEach(styleEl => {
      const cssText = styleEl.textContent || '';

      // Match class definitions like .font6 { color: red; font-weight: 700; ... }
      const classRegex = /\.([a-zA-Z0-9_-]+)\s*\{([^}]+)\}/g;
      let match;

      while ((match = classRegex.exec(cssText)) !== null) {
        const className = match[1];
        const rules = match[2];
        const styleInfo: { color?: string; fontWeight?: string } = {};

        // Extract color property
        const colorMatch = rules.match(/color\s*:\s*([^;]+);/);
        if (colorMatch) {
          let color = colorMatch[1].trim();

          // Handle special color names
          if (color === 'windowtext') {
            color = '#000000'; // Default to black
          } else if (color === 'red') {
            color = '#FF0000';
          } else if (color === 'blue') {
            color = '#0000FF';
          } else if (color === 'green') {
            color = '#008000';
          }
          // Keep other colors as-is (rgb, hex, etc.)

          styleInfo.color = color;
        }

        // Extract font-weight property
        const fontWeightMatch = rules.match(/font-weight\s*:\s*([^;]+);/);
        if (fontWeightMatch) {
          styleInfo.fontWeight = fontWeightMatch[1].trim();
        }

        if (styleInfo.color || styleInfo.fontWeight) {
          styleMap.set(className, styleInfo);
        }
      }
    });

    return styleMap;
  };

  // Pre-parse CSS styles from style tags
  const cssStyleMap = parseCssStyles(element);

  /**
   * Extracts styles (color, font-weight) from various sources (handles Windows Excel formats)
   */
  const extractStyles = (el: HTMLElement): { color?: string; fontWeight?: string } => {
    const styles: { color?: string; fontWeight?: string } = {};

    // Try inline styles first
    if (el.style.color) {
      let color = el.style.color;
      if (color === 'windowtext') {
        color = '#000000';
      }
      styles.color = color;
    }

    if (el.style.fontWeight) {
      styles.fontWeight = el.style.fontWeight;
    }

    // Try color attribute
    const colorAttr = el.getAttribute('color');
    if (colorAttr && !styles.color) {
      styles.color = colorAttr;
    }

    // Windows Excel: Check for class-based styles
    const className = el.className;
    if (className && typeof className === 'string') {
      // Check each class in the classList
      const classes = className.split(/\s+/);
      for (const cls of classes) {
        if (cssStyleMap.has(cls)) {
          const classStyle = cssStyleMap.get(cls);
          if (classStyle) {
            // Only apply color if not already set and not default black
            if (classStyle.color && !styles.color) {
              if (classStyle.color !== '#000000' && classStyle.color !== 'windowtext') {
                styles.color = classStyle.color;
              }
            }
            // Apply font-weight if not already set
            if (classStyle.fontWeight && !styles.fontWeight) {
              styles.fontWeight = classStyle.fontWeight;
            }
          }
        }
      }
    }

    return styles;
  };

  /**
   * Recursively processes DOM nodes to extract text and formatting
   */
  const processNode = (node: Node): void => {
    // Text node - extract content
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (text.trim()) {
        result.push(text);
      }
      return;
    }

    // Comment node - skip (Windows Excel has <!--StartFragment--> etc.)
    if (node.nodeType === Node.COMMENT_NODE) {
      return;
    }

    // Element node - check for formatting
    if (node.nodeType === Node.ELEMENT_NODE) {
      const el = node as HTMLElement;
      const tagName = el.tagName.toLowerCase();

      // Skip unwanted elements entirely (images, scripts, etc.)
      if (['img', 'script', 'style', 'video', 'audio', 'iframe', 'head', 'meta', 'link', 'col', 'colgroup'].includes(tagName)) {
        return;
      }

      // For table elements, just process children (strip table structure but keep content)
      if (['table', 'tbody', 'thead', 'tfoot'].includes(tagName)) {
        node.childNodes.forEach(child => processNode(child));
        return;
      }

      // Extract styles (color, font-weight) from various sources
      const extractedStyles = extractStyles(el);
      const color = extractedStyles.color;

      // Check for formatting
      const isBold = ['b', 'strong'].includes(tagName) ||
                     el.style.fontWeight === 'bold' ||
                     el.style.fontWeight === '700' ||
                     parseInt(el.style.fontWeight) >= 600 ||
                     (extractedStyles.fontWeight === 'bold') ||
                     (extractedStyles.fontWeight === '700') ||
                     (extractedStyles.fontWeight && parseInt(extractedStyles.fontWeight) >= 600);

      const isItalic = ['i', 'em'].includes(tagName) ||
                       el.style.fontStyle === 'italic';

      const isUnderline = tagName === 'u' ||
                          el.style.textDecoration?.includes('underline');

      const isStrikethrough = ['s', 'strike', 'del'].includes(tagName) ||
                              el.style.textDecoration?.includes('line-through');

      // Build opening tags for formatting
      const openTags: string[] = [];
      const closeTags: string[] = [];

      if (color) {
        openTags.push(`<span style="color: ${color}">`);
        closeTags.unshift('</span>');
      }
      if (isBold) {
        openTags.push('<strong>');
        closeTags.unshift('</strong>');
      }
      if (isItalic) {
        openTags.push('<em>');
        closeTags.unshift('</em>');
      }
      if (isUnderline) {
        openTags.push('<u>');
        closeTags.unshift('</u>');
      }
      if (isStrikethrough) {
        openTags.push('<s>');
        closeTags.unshift('</s>');
      }

      // Apply opening tags
      openTags.forEach(tag => result.push(tag));

      // Process child nodes
      node.childNodes.forEach(child => processNode(child));

      // Apply closing tags
      closeTags.forEach(tag => result.push(tag));

      // Add line break for block elements and table rows
      // Windows Excel: <br> tags within cells, <tr> for row breaks
      if (['div', 'p', 'br', 'tr', 'li'].includes(tagName)) {
        result.push('<br>');
      }
      // Add space after table cells to separate content
      else if (tagName === 'td') {
        result.push(' ');
      }
    }
  };

  processNode(element);

  // Join result and clean up
  let html = result.join('')
    // Replace multiple consecutive <br> with single <br>
    .replace(/(<br>)+/g, '<br>')
    // Replace <br> surrounded by whitespace with just <br>
    .replace(/\s*<br>\s*/g, '<br>')
    // Replace multiple spaces with single space
    .replace(/[^\S\n]+/g, ' ')
    // Trim whitespace around tags
    .replace(/\s*(<\/?[^>]+>)\s*/g, '$1')
    // Clean up spaces before closing tags
    .replace(/\s+(<\/[^>]+>)/g, '$1')
    .trim();

  // Remove leading/trailing <br> tags
  html = html.replace(/^(<br>)+|(<br>)+$/g, '');

  // Convert <br> to line breaks, then wrap in paragraphs
  if (html) {
    // Split by <br> to create paragraphs
    const lines = html.split('<br>').filter(line => line.trim());

    if (lines.length === 0) {
      return '';
    }

    // Wrap each line in a paragraph
    html = lines.map(line => `<p>${line.trim()}</p>`).join('');
  }

  return html;
};

interface RichTextEditorProps {
  /** HTML content value */
  value: string;
  /** Callback when content changes (receives sanitized HTML) */
  onChange: (html: string) => void;
  /** Placeholder text when editor is empty */
  placeholder?: string;
  /** Additional CSS classes */
  className?: string;
  /** Disable editing */
  disabled?: boolean;
}

/**
 * Rich Text Editor Component
 *
 * Provides basic text formatting (bold, italic, underline, strikethrough, colors)
 * using TipTap editor. Integrates with shadcn/ui design system.
 *
 * Features:
 * - Simple inline toolbar with formatting buttons
 * - Predefined color palette for text colors
 * - Automatic HTML sanitization (XSS protection)
 * - Backward compatible with plain text
 * - Copy/paste support from Word/Excel
 */
export const RichTextEditor = ({
  value,
  onChange,
  placeholder,
  className,
  disabled = false,
}: RichTextEditorProps) => {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder || t('richTextEditor.placeholder');

  const latestValueRef = useRef(value);
  const lastEmittedRef = useRef(sanitizeHtml(normalizeHtml(wrapPlainText(value))));
  const lastDocRef = useRef<string>('');

  useEffect(() => {
    latestValueRef.current = value;
  }, [value]);

  // Initialize TipTap editor with extensions
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable features we don't need (keep it simple)
        heading: false,
        blockquote: false,
        code: false,
        codeBlock: false,
        horizontalRule: false,
        bulletList: false,
        orderedList: false,
        listItem: false,
      }),
      TextStyle, // Required for color extension
      Color, // Text color support
      Underline, // Underline formatting
    ],
    content: wrapPlainText(value),
    editable: !disabled,
    editorProps: {
      attributes: {
        'data-placeholder': resolvedPlaceholder,
      },
      /**
       * Custom paste handler - intelligently processes pasted content
       *
       * For rich text (HTML from Excel/Word):
       * - Preserves text formatting (bold, italic, underline, strikethrough)
       * - Preserves text colors
       * - Strips tables, images, and complex formatting
       *
       * For plain text:
       * - Inserts as-is without any formatting
       */
      handlePaste: (view, event) => {
        const clipboardData = event.clipboardData;
        if (!clipboardData) return false;

        const htmlContent = clipboardData.getData('text/html');
        const textContent = clipboardData.getData('text/plain');

        // If there's HTML content, process it to extract formatting
        if (htmlContent && htmlContent.trim()) {
          event.preventDefault();

          // Create temporary element to parse HTML
          const temp = document.createElement('div');
          temp.innerHTML = htmlContent;

          // Extract and simplify formatted content
          const simplifiedHtml = extractSimplifiedFormatting(temp);

          // Insert the simplified HTML into the editor
          const { state } = view;
          const { selection } = state;
          const slice = editor?.extensionManager.extensions.find(
            (ext) => ext.name === 'document'
          )?.['storage']?.['parser']?.(simplifiedHtml) || null;

          if (slice) {
            const tr = state.tr.replaceSelection(slice);
            view.dispatch(tr);
            return true;
          }

          // Fallback: insert as plain text if parsing fails
          editor?.commands.insertContent(simplifiedHtml);
          return true;
        }

        // Plain text paste - insert without formatting
        if (textContent) {
          event.preventDefault();
          editor?.commands.insertContent(textContent);
          return true;
        }

        return false;
      },
    },
    onUpdate: ({ editor }) => {
      // Get HTML, normalize, sanitize, then call onChange (only if changed)
      const normalized = normalizeHtml(editor.getHTML());
      const sanitized = sanitizeHtml(normalized);
      if (sanitized !== latestValueRef.current) {
        onChange(sanitized);
      }
      lastEmittedRef.current = sanitized;
      lastDocRef.current = JSON.stringify(editor.getJSON());
    },
  });

  // Sync external value changes to editor
  useEffect(() => {
    if (!editor) return;

    const incoming = sanitizeHtml(normalizeHtml(wrapPlainText(value)));

    // If this update originated from the editor itself, skip reset to avoid cursor jumps
    if (incoming === lastEmittedRef.current) {
      return;
    }

    const current = sanitizeHtml(normalizeHtml(editor.getHTML()));
    const currentDoc = lastDocRef.current || JSON.stringify(editor.getJSON());

    // Build canonical doc JSON for incoming HTML to avoid resets on equivalent markup
    let incomingDoc = '';
    try {
      incomingDoc = JSON.stringify(generateJSON(incoming, editor.extensionManager.extensions));
    } catch {
      incomingDoc = '';
    }

    // If documents are semantically the same, skip
    if (incomingDoc && incomingDoc === currentDoc) {
      lastEmittedRef.current = incoming;
      return;
    }

    if (incoming !== current) {
      // Avoid emitting update to prevent cursor jumps
      editor.commands.setContent(incoming, { emitUpdate: false });
    }
    // Track latest applied content to avoid future unnecessary resets
    lastEmittedRef.current = incoming;
    lastDocRef.current = incomingDoc || JSON.stringify(editor.getJSON());
  }, [value, editor]);

  // Return null while editor is initializing
  if (!editor) {
    return null;
  }

  return (
    <div className={cn('border border-input rounded-md bg-background', className)}>
      {/* Formatting Toolbar */}
      <div className="flex items-center gap-1 border-b border-border p-2 flex-wrap">
        {/* Bold Button */}
        <Toggle
          size="sm"
          pressed={editor.isActive('bold')}
          onPressedChange={() => editor.chain().focus().toggleBold().run()}
          disabled={disabled}
          aria-label={t('richTextEditor.toggleBold')}
          title={t('richTextEditor.bold')}
        >
          <Bold className="h-4 w-4" />
        </Toggle>

        {/* Italic Button */}
        <Toggle
          size="sm"
          pressed={editor.isActive('italic')}
          onPressedChange={() => editor.chain().focus().toggleItalic().run()}
          disabled={disabled}
          aria-label={t('richTextEditor.toggleItalic')}
          title={t('richTextEditor.italic')}
        >
          <Italic className="h-4 w-4" />
        </Toggle>

        {/* Underline Button */}
        <Toggle
          size="sm"
          pressed={editor.isActive('underline')}
          onPressedChange={() => editor.chain().focus().toggleUnderline().run()}
          disabled={disabled}
          aria-label={t('richTextEditor.toggleUnderline')}
          title={t('richTextEditor.underline')}
        >
          <UnderlineIcon className="h-4 w-4" />
        </Toggle>

        {/* Strikethrough Button */}
        <Toggle
          size="sm"
          pressed={editor.isActive('strike')}
          onPressedChange={() => editor.chain().focus().toggleStrike().run()}
          disabled={disabled}
          aria-label={t('richTextEditor.toggleStrikethrough')}
          title={t('richTextEditor.strikethrough')}
        >
          <Strikethrough className="h-4 w-4" />
        </Toggle>

        {/* Divider */}
        <div className="w-px h-6 bg-border mx-1" />

        {/* Color Picker Popover - simplified for easy use */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              disabled={disabled}
              className="h-9 px-3 gap-1.5"
              title={t('richTextEditor.textColor')}
            >
              <Palette className="h-4 w-4" />
              <span className="text-xs">{t('richTextEditor.color')}</span>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-3" align="start">
            <div className="text-xs font-medium mb-2 text-muted-foreground">{t('richTextEditor.chooseTextColor')}</div>
            {/* Color Swatches Grid - larger, easier to click */}
            <div className="grid grid-cols-4 gap-2">
              {COLORS.map((color) => (
                <button
                  key={color.value}
                  className={cn(
                    'h-10 w-10 rounded-md border-2 transition-all hover:scale-110',
                    editor.isActive('textStyle', { color: color.value })
                      ? 'border-primary ring-2 ring-primary ring-offset-2'
                      : 'border-border hover:border-primary/50'
                  )}
                  style={{ backgroundColor: color.value }}
                  onClick={() => {
                    // Set color or unset if clicking black
                    if (color.value === '#000000') {
                      editor.chain().focus().unsetColor().run();
                    } else {
                      editor.chain().focus().setColor(color.value).run();
                    }
                  }}
                  title={color.name}
                  aria-label={t('richTextEditor.setTextColor', { color: color.name })}
                />
              ))}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Editor Content Area */}
      <EditorContent
        editor={editor}
        className={cn(
          'prose prose-sm max-w-none px-3 py-2 min-h-[120px]',
          'focus-within:outline-none',
          disabled && 'opacity-50 cursor-not-allowed'
        )}
      />
    </div>
  );
};
